import { Box, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { CalendarToday, ArrowForward } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { useGetTasksQuery } from "../../services/tasks.service";
import { useGetUsersQuery } from "../../services/users.service";
import { useAppTheme } from "../../context/ThemeContext";

const URGENCY_COLOR: Record<string, string> = {
  high: "#EF4444",
  medium: "#F59E0B",
  low: "#0EA5E9",
};

function dayLabel(dueDate: any): string {
  const d = moment(dueDate as any);
  const today = moment().startOf("day");
  const diff = d.startOf("day").diff(today, "days");
  if (diff < 0) return "Overdue";
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.format("ddd, MMM D");
}

function UpcomingDeadlinesWidget() {
  const { data: tasks } = useGetTasksQuery("Tasks");
  const { data: users } = useGetUsersQuery("users");
  const { isNewTheme, accentColor } = useAppTheme();
  const navigate = useNavigate();

  const userMap = Object.fromEntries((users ?? []).map((u) => [u._id, u.username]));

  const upcoming = (tasks ?? [])
    .filter((t) => {
      if (t.status === "Completed") return false;
      if (!t.dueDate) return false;
      const d = moment(t.dueDate as any);
      return d.diff(moment().startOf("day"), "days") <= 7;
    })
    .sort((a, b) => {
      const at = moment(a.dueDate as any).valueOf();
      const bt = moment(b.dueDate as any).valueOf();
      return at - bt;
    });

  // Group by day label
  const groups = upcoming.reduce<Record<string, typeof upcoming>>((acc, t) => {
    const label = dayLabel(t.dueDate);
    (acc[label] = acc[label] ?? []).push(t);
    return acc;
  }, {});

  const groupOrder = ["Overdue", "Today", "Tomorrow", ...Object.keys(groups).filter((k) => !["Overdue", "Today", "Tomorrow"].includes(k))];
  const sortedGroups = groupOrder.filter((k) => groups[k]);

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        display: "flex",
        flexDirection: "column" as const,
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : {
        width: "100%",
        borderRadius: "10px",
        boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
        display: "flex",
        flexDirection: "column" as const,
      };

  return (
    <Card sx={cardSx}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: `${accentColor}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarToday sx={{ fontSize: 17, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Upcoming Deadlines
            </Typography>
            {isNewTheme && (
              <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />
            )}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip
            label={upcoming.length}
            size="small"
            color={upcoming.filter((t) => dayLabel(t.dueDate) === "Overdue").length > 0 ? "error" : "default"}
            sx={{ fontWeight: 700, borderRadius: "4px", minWidth: 28 }}
          />
          <Link to="/tasks" style={{ textDecoration: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
              View all <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ flex: 1, overflow: "auto", p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {upcoming.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", mt: 3, fontStyle: "italic" }}>
            No upcoming deadlines
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {sortedGroups.map((label) => (
              <Box key={label}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: label === "Overdue" ? "#EF4444" : label === "Today" ? accentColor : "#64748B",
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {label}
                </Typography>
                <Stack
                  divider={<Divider sx={{ borderColor: "#F1F5F9" }} />}
                >
                  {groups[label].map((task) => {
                    const urgencyColor = URGENCY_COLOR[task.urgency as string] ?? "#64748B";
                    const assignee = userMap[task.assignedTo as string];
                    const isOverdue = label === "Overdue";

                    return (
                      <Tooltip key={task._id} title="Click to view tasks" placement="left" arrow>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          p={0.75}
                          onClick={() => navigate("/tasks")}
                          sx={
                            isNewTheme
                              ? {
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  bgcolor: isOverdue ? "#FFF5F5" : "transparent",
                                  transition: "background 0.15s ease",
                                  "&:hover": { bgcolor: isOverdue ? "#FEE2E2" : "#F8FAFC" },
                                }
                              : { cursor: "pointer" }
                          }
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={isNewTheme ? 600 : 400}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: isOverdue ? "#EF4444" : "text.primary",
                              }}
                            >
                              {task.name as string}
                            </Typography>
                            {assignee && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>
                                → {assignee}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={task.urgency as string}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              borderRadius: "4px",
                              fontSize: "0.6rem",
                              height: 18,
                              flexShrink: 0,
                              bgcolor: urgencyColor + "20",
                              color: urgencyColor,
                              border: `1px solid ${urgencyColor}40`,
                            }}
                          />
                        </Stack>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default UpcomingDeadlinesWidget;
